import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { qk } from '@/lib/queryClient'
import type {
  AdminUserPatch,
  AnnounceInput,
  InviteInput,
  ProfileInput,
} from '@/lib/types'
import { useToast } from './useToast'

export function useAdminUsers() {
  return useQuery({
    queryKey: qk.adminUsers,
    queryFn: ({ signal }) => api.admin.users(signal),
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: AdminUserPatch }) =>
      api.admin.updateUser(id, patch),
    onSuccess: (user) => {
      void queryClient.invalidateQueries({ queryKey: qk.adminUsers })
      toast.success(`Saved ${user.username}`)
    },
    onError: (error) => toast.fromError(error, 'Could not save that user'),
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ id }: { id: string; username: string }) => api.admin.deleteUser(id),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: qk.adminUsers })
      toast.success(`Deleted ${variables.username}`)
    },
    onError: (error) => toast.fromError(error, 'Could not delete that user'),
  })
}

export function useApplyProfile() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (id: string) => api.admin.applyProfile(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.adminUsers })
      toast.success('Profile re-applied in Jellyfin')
    },
    onError: (error) => toast.fromError(error, 'Could not apply the profile'),
  })
}

/** Lets a referred signup in. Enables their Jellyfin account and notifies them. */
export function useApproveUser() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (id: string) => api.admin.approveUser(id),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: qk.adminUsers })
      toast.success(`${result.user.username} approved`, 'They can sign in and watch now.')
    },
    onError: (error) => toast.fromError(error, 'Could not approve them'),
  })
}

export function useRejectUser() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (id: string) => api.admin.rejectUser(id),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: qk.adminUsers })
      toast.success(`${result.user.username} turned away`, 'The username stays reserved.')
    },
    onError: (error) => toast.fromError(error, 'Could not reject them'),
  })
}

export function useAdminInvites() {
  return useQuery({
    queryKey: qk.adminInvites,
    queryFn: ({ signal }) => api.admin.invites(signal),
  })
}

export function useCreateInvite() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (input: InviteInput) => api.admin.createInvite(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.adminInvites })
      toast.success('Invite created', 'Copy the link and share it.')
    },
    onError: (error) => toast.fromError(error, 'Could not create that invite'),
  })
}

export function useRevokeInvite() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (id: string) => api.admin.revokeInvite(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.adminInvites })
      toast.success('Invite revoked')
    },
    onError: (error) => toast.fromError(error, 'Could not revoke that invite'),
  })
}

export function useAdminProfiles() {
  return useQuery({
    queryKey: qk.adminProfiles,
    queryFn: ({ signal }) => api.admin.profiles(signal),
    staleTime: 60_000,
  })
}

export function useSaveProfile() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: ProfileInput }) =>
      id ? api.admin.updateProfile(id, input) : api.admin.createProfile(input),
    onSuccess: (profile) => {
      void queryClient.invalidateQueries({ queryKey: qk.adminProfiles })
      void queryClient.invalidateQueries({ queryKey: qk.adminUsers })
      toast.success(`Saved “${profile.name}”`)
    },
    onError: (error) => toast.fromError(error, 'Could not save that profile'),
  })
}

export function useDeleteProfile() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => api.admin.deleteProfile(id),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: qk.adminProfiles })
      toast.success(`Deleted “${variables.name}”`)
    },
    onError: (error) => toast.fromError(error, 'Could not delete that profile'),
  })
}

/**
 * The library list comes from Jellyfin (KV-cached for 5 minutes upstream), so
 * it is one of the few admin reads that fails while the home server is down.
 */
export function useJellyfinLibraries(enabled = true) {
  return useQuery({
    queryKey: qk.adminLibraries,
    queryFn: ({ signal }) => api.admin.libraries(signal),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export const ACTIVITY_PAGE_SIZE = 50

export function useAdminActivity(offset: number) {
  return useQuery({
    queryKey: qk.adminActivity(offset),
    queryFn: ({ signal }) => api.admin.activity({ limit: ACTIVITY_PAGE_SIZE, offset }, signal),
    placeholderData: keepPreviousData,
  })
}

export function useAnnounce() {
  const toast = useToast()

  return useMutation({
    mutationFn: (input: AnnounceInput) => api.admin.announce(input),
    onSuccess: (result) => {
      const count = result?.notified
      toast.success(
        'Announcement sent',
        typeof count === 'number' ? `Delivered to ${count} user${count === 1 ? '' : 's'}.` : undefined,
      )
    },
    onError: (error) => toast.fromError(error, 'Could not send the announcement'),
  })
}
